import {
  ArchiveAction,
  CorrespondenceEntityId,
  CorrespondenceEntityType,
  CorrespondenceFiltersResponse,
  CorrespondenceFormValues,
  CorrespondenceMessages,
  CorrespondenceParticipant,
  CorrespondenceParticipantsQueryParams,
  CorrespondenceThread,
  CreateCorrespondenceResponse,
  entityTypeToApiPrefix,
  Filters,
  getApiClient,
  GetCorrespondenceResponse,
  RespondToCorrespondenceRequest,
  RespondToCorrespondenceResponse,
} from "toptaal-shared"

export async function getFilteredCorrespondenceInbox({
  filters,
}: {
  filters: Filters
}) {
  const apiClient = getApiClient()

  return await apiClient
    .post<CorrespondenceFiltersResponse>("correspondences/inbox", {
      json: filters,
    })
    .json()
}

export async function getFilteredCorrespondence({
  entityId,
  entityType,
  filters,
}: {
  entityId: CorrespondenceEntityId
  entityType: CorrespondenceEntityType
  filters: Filters
}) {
  const apiClient = getApiClient()

  return await apiClient
    .post<CorrespondenceFiltersResponse>(
      `${entityTypeToApiPrefix[entityType]}/${entityId}/correspondences/filters`,
      {
        json: filters,
      },
    )
    .json()
}

export async function createCorrespondence({
  entityId,
  entityType,
  data,
}: {
  entityId: CorrespondenceEntityId
  entityType: CorrespondenceEntityType
  data: CorrespondenceFormValues
}) {
  const apiClient = getApiClient()

  const payload = {
    correspondenceThread: {
      subject: data.subject,
      visibleForTeachers: data.visibleForTeachers,
      visibleForOrganizationUsers: data.visibleForOrganizationUsers,
      type: data.about,
      correspondences: [
        {
          content: data.message,
        },
      ],
    },
  }

  return await apiClient
    .post<CreateCorrespondenceResponse>(
      `${entityTypeToApiPrefix[entityType]}/${entityId}/correspondences`,
      {
        json: payload,
      },
    )
    .json()
}

export async function updateCorrespondence({
  entityId,
  entityType,
  threadId,
  data,
}: {
  entityId: CorrespondenceEntityId
  entityType: CorrespondenceEntityType
  threadId: CorrespondenceThread["id"]
  data: Pick<
    CorrespondenceFormValues,
    "visibleForTeachers" | "visibleForOrganizationUsers"
  >
}) {
  const apiClient = getApiClient()

  const payload = {
    correspondenceThread: {
      visibleForTeachers: data.visibleForTeachers,
      visibleForOrganizationUsers: data.visibleForOrganizationUsers,
    },
  }

  return await apiClient
    .put(
      `${entityTypeToApiPrefix[entityType]}/${entityId}/correspondences/${threadId}`,
      {
        json: payload,
      },
    )
    .json()
}

export async function getCorrespondence({
  entityId,
  entityType,
  threadId,
}: {
  entityId: CorrespondenceEntityId
  entityType: CorrespondenceEntityType
  threadId: CorrespondenceThread["id"]
}) {
  const apiClient = getApiClient()

  return await apiClient
    .get<GetCorrespondenceResponse>(
      `${entityTypeToApiPrefix[entityType]}/${entityId}/correspondences/${threadId}`,
    )
    .json()
}

export async function getCorrespondenceMessages({
  entityId,
  entityType,
  threadId,
  filters,
}: {
  entityId: CorrespondenceEntityId
  entityType: CorrespondenceEntityType
  threadId: number
  filters: Filters
}) {
  const apiClient = getApiClient()

  return await apiClient
    .post<CorrespondenceMessages>(
      `${entityTypeToApiPrefix[entityType]}/${entityId}/correspondences/${threadId}/filters`,
      {
        json: filters,
      },
    )
    .json()
}

export async function getCorrespondenceParticipants({
  entityId,
  entityType,
  type,
}: CorrespondenceParticipantsQueryParams) {
  const apiClient = getApiClient()
  if (!entityId || !entityType) {
    return { participants: [] }
  }

  return await apiClient
    .post<{
      participants: CorrespondenceParticipant[]
    }>(
      `${entityTypeToApiPrefix[entityType]}/${entityId}/correspondences/participants`,
      {
        json: {
          entityId,
          entityType,
          type,
        },
      },
    )
    .json()
}

export async function respondToCorrespondence({
  entityId,
  entityType,
  threadId,
  data,
}: {
  entityId: CorrespondenceEntityId
  entityType: CorrespondenceEntityType
  threadId: CorrespondenceThread["id"]
  data: RespondToCorrespondenceRequest
}) {
  const apiClient = getApiClient()

  return await apiClient
    .post<RespondToCorrespondenceResponse>(
      `${entityTypeToApiPrefix[entityType]}/${entityId}/correspondences/${threadId}/respond`,
      {
        json: data,
      },
    )
    .json()
}

export async function markCorrespondencesAsRead(threadIds: number[]) {
  const apiClient = getApiClient()

  return await apiClient
    .post("correspondences/read", {
      json: {
        correspondence_thread_ids: threadIds,
      },
    })
    .json()
}

export async function archiveCorrespondences(
  threadIds: number[],
  action?: ArchiveAction,
) {
  const apiClient = getApiClient()

  return await apiClient
    .post("correspondences/archive", {
      json: {
        correspondenceThreadIds: threadIds,
        action,
      },
    })
    .json()
}

export async function getCorrespondenceUnreadCount() {
  const apiClient = getApiClient()

  return await apiClient
    .get<{ unreadCount: number }>("correspondences/unread_count")
    .json()
}
